// store/integrations/reducer.ts
import { Reducer } from "redux";
import {
  IntegrationState,
  IntegrationActionTypes,
  AppDTO,
  AppVersionDTO,
  InstallationDTO,
  AccountDTO,
  PageDTO,
  FormDTO,
  FieldDTO,
  FieldMappingItem,
  MappingRecord
} from "./types";

const initialState: IntegrationState = {
  apps: [],
  loadingApps: false,
  installations: [],
  loadingInstalls: false,
  currentApp: null,
  loadingDetail: false,
  versionsByAppId: {},
  loadingVersions: false,
  loadingOAuthComplete: false,
  errorOAuthComplete: undefined,
  oauthCompleteData: undefined,
  metadata: undefined,
  loadingMetadata: false,
  errorMetadata: undefined,
  accounts: [],
  loadingAccounts: false,
  errorAccounts: undefined,
  pages: [],
  loadingPages: false,
  errorPages: undefined,
  forms: [],
  loadingForms: false,
  fields: [],
  loadingFields: false,
  errorFields: undefined,
  mapping: [],
  loadingMapping: false,
  errorMapping: undefined,
  currentMappingRecord: undefined,
  loadingCurrentMappingRecord: false,
  errorCurrentMappingRecord: undefined,
  mappingRecords: [],
  loadingMappingRecords: false,
  errorMappingRecords: undefined,
  discovery: {},
  loadingDiscovery: {},
  errorDiscovery: {},
  loadingOperation: false,
  errorOperation: undefined,
};

export const IntegrationReducer: Reducer<IntegrationState> = (
  state = initialState,
  action: any
) => {
  switch (action.type) {
    // LIST APPS
    case IntegrationActionTypes.GET_APPS_REQUEST:
      return { ...state, loadingApps: true, errorApps: undefined };
    case IntegrationActionTypes.GET_APPS_SUCCESS:
      return { ...state, loadingApps: false, apps: action.payload.content };
    case IntegrationActionTypes.GET_APPS_ERROR:
      return { ...state, loadingApps: false, errorApps: action.error.message };

    // LIST INSTALLS
    case IntegrationActionTypes.GET_INSTALLS_REQUEST:
      return { ...state, loadingInstalls: true, errorInstalls: undefined };
    case IntegrationActionTypes.GET_INSTALLS_SUCCESS:
      return { ...state, loadingInstalls: false, installations: action.payload.content };
    case IntegrationActionTypes.GET_INSTALLS_ERROR:
      return { ...state, loadingInstalls: false, errorInstalls: action.error.message };

    // GET SINGLE APP
    case IntegrationActionTypes.GET_APP_REQUEST:
      return { ...state, loadingDetail: true, errorDetail: undefined };
    case IntegrationActionTypes.GET_APP_SUCCESS:
      return { ...state, loadingDetail: false, currentApp: action.payload as AppDTO };
    case IntegrationActionTypes.GET_APP_ERROR:
      return { ...state, loadingDetail: false, errorDetail: action.error.message };

    // LIST VERSIONS
    case IntegrationActionTypes.GET_VERSIONS_REQUEST:
      return { ...state, loadingVersions: true, errorVersions: undefined };
    case IntegrationActionTypes.GET_VERSIONS_SUCCESS: {
      const versions = action.payload.content as AppVersionDTO[];
      const appId = versions[0]?.app_id!;
      return {
        ...state,
        loadingVersions: false,
        versionsByAppId: { ...state.versionsByAppId, [appId]: versions }
      };
    }
    case IntegrationActionTypes.GET_VERSIONS_ERROR:
      return { ...state, loadingVersions: false, errorVersions: action.error.message };

    // POST INSTALL (we’ll keep using global loading for install/uninstall)
    case IntegrationActionTypes.POST_INSTALL_REQUEST:
      return state;
    case IntegrationActionTypes.POST_INSTALL_SUCCESS:
      return {
        ...state,
        installations: [...state.installations, action.payload as InstallationDTO]
      };
    case IntegrationActionTypes.POST_INSTALL_ERROR:
      return state;

    case IntegrationActionTypes.DELETE_INSTALL_REQUEST:
      return state;
    case IntegrationActionTypes.DELETE_INSTALL_SUCCESS:
      return {
        ...state,
        installations: state.installations.filter(i => i.id !== (action.payload as any).id)
      };
    case IntegrationActionTypes.DELETE_INSTALL_ERROR:
      return state;
    case IntegrationActionTypes.GET_METADATA_REQUEST:
      return { ...state, loadingMetadata: true, errorMetadata: undefined };
    case IntegrationActionTypes.GET_METADATA_SUCCESS:
      return { ...state, loadingMetadata: false, metadata: action.payload };
    case IntegrationActionTypes.GET_METADATA_ERROR:
      return { ...state, loadingMetadata: false, errorMetadata: action.error.message };

    case IntegrationActionTypes.GET_ACCOUNTS_REQUEST:
      return { ...state, loadingAccounts: true, errorAccounts: undefined };
    case IntegrationActionTypes.GET_ACCOUNTS_SUCCESS:
      return { ...state, loadingAccounts: false, accounts: action.payload as AccountDTO[] };
    case IntegrationActionTypes.GET_ACCOUNTS_ERROR:
      return { ...state, loadingAccounts: false, errorAccounts: action.error.message };

    case IntegrationActionTypes.GET_PAGES_REQUEST:
      return { ...state, loadingPages: true, errorPages: undefined };
    case IntegrationActionTypes.GET_PAGES_SUCCESS:
      return { ...state, loadingPages: false, pages: action.payload as PageDTO[] };
    case IntegrationActionTypes.GET_PAGES_ERROR:
      return { ...state, loadingPages: false, errorPages: action.error.message };

    // FORMS (wizard)
    case IntegrationActionTypes.GET_FORMS_REQUEST:
      return { ...state, loadingForms: true, errorForms: undefined };
    case IntegrationActionTypes.GET_FORMS_SUCCESS:
      return { ...state, loadingForms: false, forms: action.payload as FormDTO[] };
    case IntegrationActionTypes.GET_FORMS_ERROR:
      return { ...state, loadingForms: false, errorForms: action.error.message };
    case IntegrationActionTypes.COMPLETE_OAUTH_REQUEST:
      return {
        ...state,
        loadingOAuthComplete: true,
        errorOAuthComplete: undefined,
        oauthCompleteData: undefined,
      };
    case IntegrationActionTypes.COMPLETE_OAUTH_SUCCESS:
      return {
        ...state,
        loadingOAuthComplete: false,
        oauthCompleteData: action.payload as { provider: string; id: string },
      };
    case IntegrationActionTypes.COMPLETE_OAUTH_ERROR:
      return {
        ...state,
        loadingOAuthComplete: false,
        errorOAuthComplete: action.error.message,
      };
    // FIELDS
    case IntegrationActionTypes.GET_FIELDS_REQUEST:
      return { ...state, loadingFields: true, errorFields: undefined };
    case IntegrationActionTypes.GET_FIELDS_SUCCESS:
      return { ...state, loadingFields: false, fields: action.payload as FieldDTO[] };
    case IntegrationActionTypes.GET_FIELDS_ERROR:
      return { ...state, loadingFields: false, errorFields: action.error.message };

    // GET EXISTING MAPPING
    case IntegrationActionTypes.GET_MAPPING_REQUEST:
      return { ...state, loadingMapping: true, errorMapping: undefined };
    case IntegrationActionTypes.GET_MAPPING_SUCCESS:
      return { ...state, loadingMapping: false, mapping: action.payload as FieldMappingItem[] };
    case IntegrationActionTypes.GET_MAPPING_ERROR:
      return { ...state, loadingMapping: false, errorMapping: action.error.message };

    case IntegrationActionTypes.GET_MAPPING_RECORD_REQUEST:
      return {
        ...state,
        loadingCurrentMappingRecord: true,
        errorCurrentMappingRecord: undefined,
      };
    case IntegrationActionTypes.GET_MAPPING_RECORD_SUCCESS:
      return {
        ...state,
        loadingCurrentMappingRecord: false,
        currentMappingRecord: action.payload as MappingRecord,
      };
    case IntegrationActionTypes.GET_MAPPING_RECORD_ERROR:
      return {
        ...state,
        loadingCurrentMappingRecord: false,
        errorCurrentMappingRecord: action.error,
      };

    // SAVE MAPPING
    case IntegrationActionTypes.SAVE_MAPPING_REQUEST:
      return { ...state, loadingMapping: true, errorMapping: undefined };
    case IntegrationActionTypes.SAVE_MAPPING_SUCCESS:
      return { ...state, loadingMapping: false, mapping: action.payload as FieldMappingItem[] };
    case IntegrationActionTypes.SAVE_MAPPING_ERROR:
      return { ...state, loadingMapping: false, errorMapping: action.error.message };

    case IntegrationActionTypes.GET_MAPPING_RECORDS_REQUEST:
      return { ...state, loadingMappingRecords: true, errorMappingRecords: undefined };
    case IntegrationActionTypes.GET_MAPPING_RECORDS_SUCCESS:
      return {
        ...state,
        loadingMappingRecords: false,
        mappingRecords: action.payload as MappingRecord[],
      };
    case IntegrationActionTypes.GET_MAPPING_RECORDS_ERROR:
      return {
        ...state,
        loadingMappingRecords: false,
        errorMappingRecords: action.error.message,
      };

    case IntegrationActionTypes.CREATE_MAPPING_RECORD_SUCCESS:
      return {
        ...state,
        mappingRecords: [...state.mappingRecords, action.payload],
      };
    case IntegrationActionTypes.UPDATE_MAPPING_RECORD_SUCCESS:
      return {
        ...state,
        mappingRecords: state.mappingRecords?.map(r =>
          r.id === action.payload.id ? action.payload : r
        ),
      };
    case IntegrationActionTypes.DELETE_MAPPING_RECORD_SUCCESS:
      return {
        ...state,
        mappingRecords: state.mappingRecords?.filter(r => r.id !== action.payload),
      };

    case IntegrationActionTypes.GET_DISCOVERY_REQUEST: {
      const op = action.meta.opName as string;
      return {
        ...state,
        loadingDiscovery: { ...state.loadingDiscovery, [op]: true },
        errorDiscovery: { ...state.errorDiscovery, [op]: undefined }
      };
    }
    case IntegrationActionTypes.GET_DISCOVERY_SUCCESS: {
      const op = action.meta.opName as string;
      return {
        ...state,
        loadingDiscovery: { ...state.loadingDiscovery, [op]: false },
        discovery: { ...state.discovery, [op]: action.payload },
      };
    }
    case IntegrationActionTypes.GET_DISCOVERY_ERROR: {
      const op = action.meta.opName as string;
      return {
        ...state,
        loadingDiscovery: { ...state.loadingDiscovery, [op]: false },
        errorDiscovery: { ...state.errorDiscovery, [op]: action.error }
      };
    }

    case IntegrationActionTypes.INVOKE_OPERATION_REQUEST:
      return { ...state, loadingOperation: true, errorOperation: undefined };
    case IntegrationActionTypes.INVOKE_OPERATION_SUCCESS:
      return { ...state, loadingOperation: false };
    case IntegrationActionTypes.INVOKE_OPERATION_ERROR:
      return { ...state, loadingOperation: false, errorOperation: action.error };
    default:
      return state;
  }
};
